import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { CardContent, Link, Typography, Table, TableBody, TableCell, TableRow, TableContainer, Button, Grid, Dialog, DialogActions, DialogContent, TextField, DialogTitle, IconButton, Collapse, Box, List, Skeleton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FavoriteBorder as FavoriteBorderIcon, Favorite as FavoriteIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useGetReviewsQuery } from '../features/api/rest/apiReviewsSlice';
import { useAddNewQuestionMutation } from '../features/api/rest/apiQuestionsSlice';
import Reviews from './CheckReviews';
import CheckDataProduct from '../js/CheckDataProduct';
import Questions from './QuestionsResults';
import ProductShowFeatures from './ProductShowFeatures';
import { useGetEditProductUrlQuery } from '../features/api/openFoodFacts/apiFoodFactsSlice';
import { useGetProfileQuery } from '../features/api/rest/apiProfileSlice';
import { useGetFavoriteQuery, useHandleFavoriteMutation } from '../features/api/rest/apiFavoritesSlice';
import NutritionalValueTable from './NutritionalValueTable';
import { setAlertMessage } from '../features/layoutSlice';
import Spinner from './Spinner';
import CheckBoard from './CheckBoard';
import CodeInformation from './CodeInformation';
import LotInformation from './LotInformation';
import parseCodeString from '../utils/parseCodeString';

const languages = window.navigator.languages.map((item) => item.replace(/-.*/, '')).filter((item, pos, self) => self.indexOf(item) === pos);
const CheckData = new CheckDataProduct(languages);

function ProductDetails({ product }) {
  const ExpandMore = styled((props) => {
    // eslint-disable-next-line react/prop-types
    const { expand, ...other } = props;
    return <IconButton {...other} />; //    eslint-disable-line react/jsx-props-no-spreading
  })(({ theme, expand }) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  }));

  const { ean } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [expandedInformation, setExpandedInformation] = useState(false);
  const [expandedBoard, setExpandedBoard] = useState(false);
  const [expandedLots, setExpandedLots] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);

  const { favorite } = useGetFavoriteQuery(ean).data || false;

  const { status } = useGetProfileQuery(undefined, { refetchOnMountOrArgChange: true });

  const { count = 0 } = useGetReviewsQuery(
    { ean, mine: true },
    {
      selectFromResult: ({ data }) => ({
        count: data?.count,
      }),
    }
  );

  const [searchParams] = useSearchParams();
  const [lots, setLots] = useState([]);
  const [isLoadingLots, setIsLoadingLots] = useState(true);
  const parseLots = async () => {
    setIsLoadingLots(true);
    const parsedLots = parseCodeString(`${ean};${searchParams.get('lots')}` || '') || [];
    setLots(parsedLots.attributes.map((attribute) => ({ authority: attribute.key, code: attribute.value, ean })));
  };
  useEffect(() => {
    parseLots().then(() => setIsLoadingLots(false));
  }, [searchParams]);

  useEffect(() => {
    setIsFavorite(favorite);
  }, [favorite]);

  const [addNewQuestion, { isLoading }] = useAddNewQuestionMutation();
  const [handleFavorite] = useHandleFavoriteMutation();

  const { currentData: editProductUrl } = useGetEditProductUrlQuery(ean);

  const handleClick = () => {
    navigate(`/products/${product._id}/editOrCreateReview`); // eslint-disable-line no-underscore-dangle
  };

  const handleQuestionClick = () => {
    setOpen(!open);
  };

  const handleEditProduct = () => {
    window.location.replace(editProductUrl);
  };

  const handlePostQuestion = async (e) => {
    e.preventDefault();
    try {
      await addNewQuestion({ payload: { body: question }, ean }).unwrap();
      setQuestion('');
      setOpen(false);
      window.location.reload();
    } catch (err) {
      console.error(err); // todo: check err.status and provide feedback to the user
    }
  };

  const setFavorite = async (e) => {
    e.preventDefault();
    try {
      await handleFavorite({ ean, favorite: !isFavorite }).unwrap();
      setIsFavorite(!isFavorite);
    } catch (err) {
      dispatch(setAlertMessage({ text: t('common.messages.notAutorized.feature'), type: 'error' }));
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <>
      <Grid container spacing={2} sx={{ textAlign: 'center' }}>
        <Grid item xs={12}>
          <Typography variant="h4" component="h1">
            {CheckData.checkDataMultiLanguage(product, 'product_name', t('products.messages.nameNotPresent'))} {CheckDataProduct.checkMultiFields([product.quantity_imported, product.quantity])}
            <IconButton onClick={setFavorite}>{isFavorite ? <FavoriteIcon htmlColor="#EE2B0C" /> : <FavoriteBorderIcon />}</IconButton>
            <br />
            <Typography variant="h6">{CheckDataProduct.checkDataBrands(product.brands)}</Typography>
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {(CheckDataProduct.checkData(product.image_front_url) && <img src={product.image_front_url} alt={t('products.details.productImage')} />) || (
            <CardContent sx={{ textAlign: 'center', backgroundColor: 'gray', color: 'white', height: '8em' }}>
              <Typography sx={{ mt: '2em' }}>{t('products.messages.imageNotPresent')}</Typography>
            </CardContent>
          )}
        </Grid>
      </Grid>
      {searchParams.get('lots') && (
        <Box>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('products.lots.title')}
            <ExpandMore expand={expandedLots} onClick={() => setExpandedLots(!expandedLots)} aria-expanded={expandedLots} aria-label={t('aria.icons.showMore')} data-testid="ExpandMore-Lots">
              <ExpandMoreIcon data-testid="ExpandMoreIcon-Lots" />
            </ExpandMore>
          </Typography>
          <Collapse in={expandedLots}>
            {isLoadingLots ? (
              <>
                <Skeleton />
                <Skeleton />
                <Skeleton />
              </>
            ) : (
              <List component="div">
                {lots &&
                  lots.map((lot) =>
                    lot.authority === '' ? <CodeInformation ean={lot.ean} code={lot.code} key={`${lot.ean}::${lot.code}`} root /> : <LotInformation ean={lot.ean} authority={lot.authority} code={lot.code} key={`${lot.ean}:${lot.authority}:${lot.code}`} root />
                  )}
              </List>
            )}
          </Collapse>
        </Box>
      )}
      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
        {t('products.board.title')}
        <ExpandMore expand={expandedBoard} onClick={() => setExpandedBoard(!expandedBoard)} aria-expanded={expandedBoard} aria-label={t('aria.icons.showMore')} data-testid="ExpandMore-Board">
          <ExpandMoreIcon data-testid="ExpandMoreIcon-Board" />
        </ExpandMore>
      </Typography>
      <Collapse in={expandedBoard} timeout="auto" unmountOnExit>
        <CheckBoard />
      </Collapse>
      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
        {t('common.actions.moreInformation')}
        <ExpandMore expand={expandedInformation} onClick={() => setExpandedInformation(!expandedInformation)} aria-expanded={expandedInformation} aria-label={t('aria.icons.showMore')} data-testid="ExpandMore-Information">
          <ExpandMoreIcon data-testid="ExpandMoreIcon-Information" />
        </ExpandMore>
      </Typography>
      <Collapse in={expandedInformation} timeout="auto" unmountOnExit>
        <TableContainer sx={{ mb: 2 }}>
          <Table aria-label={t('aria.tables.productDetails')}>
            <TableBody>
              <TableRow>
                <TableCell component="th" scope="row" variant="head">
                  {t('products.research.details.brands')}
                </TableCell>
                <TableCell>{CheckDataProduct.checkDataBrands(product.brands, t('products.messages.brandNotPresent'))}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row" variant="head">
                  {t('products.research.details.allergens')}
                </TableCell>
                <TableCell>{CheckDataProduct.checkAndCleanDataSingleLanguage(product.allergens, t('products.messages.allergensNotPresent'))}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row" variant="head">
                  {t('products.research.details.quantity')}
                </TableCell>
                <TableCell>{CheckDataProduct.checkDataSingleLanguage(product.quantity_imported, t('products.messages.quantityNotPresent'))}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row" variant="head">
                  {t('products.research.details.awardsCertificationsRecognitions')}
                </TableCell>
                <TableCell>{CheckDataProduct.checkAndCleanDataSingleLanguage(product.labels, t('products.messages.awardsCertificationsRecognitionsNotPresent'))}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row" variant="head">
                  {t('products.research.details.packaging')}
                </TableCell>
                <TableCell>{CheckDataProduct.checkMultiData(product.packagings, t('products.messages.packagingNotPresent'))}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row" variant="head">
                  Nutri-Score
                </TableCell>
                <TableCell sx={{ textTransform: 'uppercase' }}>{CheckDataProduct.checkDataSingleLanguage(product.nutriscore_grade, t('products.messages.nutriScoreNotPresent'))}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row" variant="head">
                  Eco-Score
                </TableCell>
                <TableCell sx={{ textTransform: 'uppercase' }}>{CheckDataProduct.checkDataSingleLanguage(product.ecoscore_grade, t('products.messages.ecoScoreNotPresent'))}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row" variant="head">
                  {t('products.research.details.manufacturerSite')}
                </TableCell>
                <TableCell>{(CheckDataProduct.checkData(product.link) && <Link href={product.link}>{product.link}</Link>) || t('products.messages.manufacturerSiteNotPresent')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row" variant="head">
                  {t('products.research.details.ingredientAnalysis')}
                </TableCell>
                <TableCell>{CheckDataProduct.checkSingleDataOnArray(product.ingredients_analysis_tags, t('products.messages.ingredientAnalysisNotPresent'))}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
      <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
        {t('products.research.details.ingredients')}:
        <Typography variant="body1" sx={{ pt: 1 }}>
          {CheckData.checkDataMultiLanguage(product, 'ingredients_text', t('products.messages.ingredientsNotPresent'))}
        </Typography>
      </Typography>
      <NutritionalValueTable nutriments={product.nutriments} servingSize={product.serving_size} />
      <ProductShowFeatures ean={ean} />
      <Grid sx={{ mt: 1.5 }}>
        <Button variant="contained" onClick={handleEditProduct} sx={{ mr: 1, maxWidth: { xs: '8em', sm: '100%' } }}>
          {t('products.research.foundSingle.editProduct')}
        </Button>
        <Button variant="contained" onClick={handleClick} sx={{ ml: 1, maxWidth: { xs: '8em', sm: '100%' } }}>
          {t(count === 1 ? 'reviews.edit' : 'reviews.add')}
        </Button>
      </Grid>
      <Typography sx={{ mt: 3, textAlign: 'left', fontWeight: 'bold', fontSize: '25px' }}>
        {t('questionsAnswers.heading')}
        {status === 'fulfilled' && (
          <Button variant="contained" onClick={handleQuestionClick} sx={{ ml: 1 }}>
            {t('questionsAnswers.addQuestion')}
          </Button>
        )}
      </Typography>
      <Questions sx={{ pb: 10 }} />
      <Typography sx={{ textAlign: 'left', fontWeight: 'bold', fontSize: '25px' }}>{t('reviews.heading')}</Typography>
      <Dialog open={open} onClose={handleQuestionClick} fullWidth maxWidth="sm">
        {/* onClose is fired when the user clicks out of the pop-up window */}
        <DialogTitle>{t('questionsAnswers.addQuestion')}</DialogTitle>
        <DialogContent>
          <TextField margin="dense" id="question" label={t('questionsAnswers.questionText')} type="text" fullWidth variant="standard" required onChange={(event) => setQuestion(event.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleQuestionClick}>{t('common.actions.cancel')}</Button>
          <Button onClick={handlePostQuestion}>{t('common.actions.post')}</Button>
        </DialogActions>
      </Dialog>
      <Reviews />
    </>
  );
}

ProductDetails.propTypes = {
  product: PropTypes.shape({
    _id: PropTypes.string,
    allergens: PropTypes.string,
    product_name: PropTypes.string,
    brands: PropTypes.string,
    ingredients_text: PropTypes.string,
    image_front_url: PropTypes.string,
    quantity_imported: PropTypes.string,
    quantity: PropTypes.string,
    labels: PropTypes.string,
    nutriments: PropTypes.shape({}),
    packagings: PropTypes.array, // eslint-disable-line react/forbid-prop-types
    ingredients_analysis_tags: PropTypes.array, // eslint-disable-line react/forbid-prop-types
    nutriscore_grade: PropTypes.string,
    ecoscore_grade: PropTypes.string,
    link: PropTypes.string,
    serving_size: PropTypes.string,
  }).isRequired,
};

export default ProductDetails;
